/**
 * @module imageResolverService
 * @description Service for resolving image references to actual data
 *
 * Handles the mapping between filename references in markdown
 * and the actual base64 data URLs stored in IndexedDB.
 *
 * Image references use simple filenames: "figure-1-1.jpg"
 * These are resolved to base64 data URLs for display.
 */

import { get } from "svelte/store";
import { images } from "../stores/appStore";
import type { Image } from "../types/models";

/**
 * Internal cache of images for use in non-reactive contexts (like ProseMirror)
 * This gets updated whenever images are accessed
 */
let imageCache: Image[] = [];

/**
 * Update the internal image cache from the store
 */
export function updateImageCache(): void {
  imageCache = get(images);
}

/**
 * Set image cache directly (used by plugins that have the images array)
 */
export function setImageCache(imgs: Image[]): void {
  imageCache = imgs;
}

/**
 * Check if a URL is a project image reference (simple filename, not a full URL or data URL)
 */
export function isProjectImageRef(url: string): boolean {
  // Not a data URL, not an http(s) URL, and has an image extension
  if (
    url.startsWith("data:") ||
    url.startsWith("http://") ||
    url.startsWith("https://")
  ) {
    return false;
  }
  // Check for common image extensions
  return /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i.test(url);
}

/**
 * Create a reference from a filename (just returns the filename as-is)
 */
export function createMediaReference(filename: string): string {
  return filename;
}

/**
 * Find an image by filename in the current project's images
 */
export function findImageByFilename(filename: string): Image | undefined {
  // Update cache first
  updateImageCache();
  return imageCache.find((img) => img.filename === filename);
}

/**
 * Find an image by ID
 */
export function findImageById(id: string): Image | undefined {
  updateImageCache();
  return imageCache.find((img) => img.id === id);
}

/**
 * Resolve an image reference to a base64 data URL
 * Returns the original URL if not a project image or if image not found
 */
export function resolveImageUrl(url: string): string {
  if (!isProjectImageRef(url)) {
    return url;
  }

  const image = findImageByFilename(url);
  if (!image) {
    return url;
  }

  return image.dataUrl;
}

/**
 * Resolve all image filename references in markdown content to base64
 * Useful for export or preview
 */
export function resolveAllImageUrls(markdown: string): string {
  updateImageCache();

  // Create a map of filename -> dataUrl for faster lookup
  const imageMap = new Map<string, string>();
  for (const img of imageCache) {
    imageMap.set(img.filename, img.dataUrl);
  }

  // Replace all filename references with base64 data URLs
  // Matches: ![alt](filename.ext) or ![alt](filename.ext "title")
  return markdown.replace(
    /!\[([^\]]*)\]\(([^)"]+\.(jpg|jpeg|png|gif|webp|svg|bmp))(\s+"[^"]*")?\)/gi,
    (match, alt, filename, ext, title) => {
      const dataUrl = imageMap.get(filename);
      if (dataUrl) {
        return `![${alt}](${dataUrl}${title || ""})`;
      }
      return match; // Keep original if not found
    },
  );
}

/**
 * Convert base64 data URLs back to filename references in markdown
 * Used when loading content that was saved with embedded images
 */
export function convertDataUrlsToFilenames(markdown: string): string {
  updateImageCache();

  // Create a map of dataUrl -> filename for reverse lookup
  const reverseMap = new Map<string, string>();
  for (const img of imageCache) {
    reverseMap.set(img.dataUrl, img.filename);
  }

  // Replace data URLs with filename references
  return markdown.replace(
    /!\[([^\]]*)\]\((data:image\/[^)]+)\)/g,
    (match, alt, dataUrl) => {
      const filename = reverseMap.get(dataUrl);
      if (filename) {
        return `![${alt}](${filename})`;
      }
      return match; // Keep original if not in project images
    },
  );
}

/**
 * Get all image filename references from markdown content
 */
export function extractImageReferences(markdown: string): string[] {
  const refs: string[] = [];
  const regex =
    /!\[[^\]]*\]\(([^)"]+\.(jpg|jpeg|png|gif|webp|svg|bmp))(\s+"[^"]*")?\)/gi;
  let match;

  while ((match = regex.exec(markdown)) !== null) {
    refs.push(match[1]);
  }

  return refs;
}

/**
 * Check if all image references in content can be resolved
 */
export function validateImageReferences(markdown: string): {
  valid: boolean;
  missing: string[];
} {
  const refs = extractImageReferences(markdown);
  const missing: string[] = [];

  for (const filename of refs) {
    if (!findImageByFilename(filename)) {
      missing.push(filename);
    }
  }

  return {
    valid: missing.length === 0,
    missing,
  };
}

export default {
  isProjectImageRef,
  createMediaReference,
  findImageByFilename,
  findImageById,
  resolveImageUrl,
  resolveAllImageUrls,
  convertDataUrlsToFilenames,
  extractImageReferences,
  validateImageReferences,
  updateImageCache,
  setImageCache,
};
